import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "../utils";
import { Icon } from "../icon";
import { buttonVariants } from "./button.variants";
import type { ButtonProps } from "./button.types";

/**
 * Button Component
 *
 * V&A Waterfront Design System - Primary Button
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=9858-7266
 *
 * @example
 * // Basic usage
 * <Button variant="blue-filled">Click me</Button>
 *
 * // With icons
 * <Button iconLeft="chevron-left" iconRight="arrow-right">Navigate</Button>
 *
 * // As anchor
 * <Button asChild variant="blue-outline">
 *   <a href="/page">Link Button</a>
 * </Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((t0, ref) => {
  const $ = _c(34);
  let children;
  let className;
  let disabled;
  let iconLeft;
  let iconRight;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      iconLeft,
      iconRight,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = iconLeft;
    $[5] = iconRight;
    $[6] = props;
    $[7] = size;
    $[8] = t1;
    $[9] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    iconLeft = $[4];
    iconRight = $[5];
    props = $[6];
    size = $[7];
    t1 = $[8];
    variant = $[9];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  const t2 = asChild ? undefined : "button";
  let t3;
  if ($[10] !== className || $[11] !== size || $[12] !== variant) {
    t3 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[10] = className;
    $[11] = size;
    $[12] = variant;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = asChild ? undefined : disabled;
  const t5 = disabled || undefined;
  const t6 = disabled || undefined;
  const t7 = disabled ? -1 : undefined;
  let t8;
  if ($[14] !== iconLeft) {
    t8 = iconLeft && <Icon name={iconLeft} />;
    $[14] = iconLeft;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== asChild || $[17] !== children) {
    t9 = asChild ? <Slottable>{children}</Slottable> : <span>{children}</span>;
    $[16] = asChild;
    $[17] = children;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== iconRight) {
    t10 = iconRight && <Icon name={iconRight} />;
    $[19] = iconRight;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== Comp || $[22] !== props || $[23] !== ref || $[24] !== t10 || $[25] !== t2 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t11 = <Comp ref={ref} data-slot="button" type={t2} className={t3} disabled={t4} aria-disabled={t5} data-disabled={t6} tabIndex={t7} {...props}>{t8}{t9}{t10}</Comp>;
    $[21] = Comp;
    $[22] = props;
    $[23] = ref;
    $[24] = t10;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
});
Button.displayName = "Button";
export { Button };